import React, { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { get, ref, update } from 'firebase/database';
import { realtimeDb } from '../../firebase';
import { FaArrowLeft, FaPrint, FaFileAlt, FaFileContract, FaFileSignature, FaEdit, FaSave } from 'react-icons/fa';
import { kvaOptions } from '../../constants';
import { normalizeCompanyKey, firebaseCompanyKey } from '../../Utils/companyUtils';
import { formatDate } from '../../Utils/dateUtils';

// Utility function to convert numbers to words
const numberToWords = (num) => {
  const ones = ['', 'one', 'two', 'three', 'four', 'five', 'six', 'seven', 'eight', 'nine',
    'ten', 'eleven', 'twelve', 'thirteen', 'fourteen', 'fifteen',
    'sixteen', 'seventeen', 'eighteen', 'nineteen'];
  const tens = ['', '', 'twenty', 'thirty', 'forty', 'fifty', 'sixty', 'seventy', 'eighty', 'ninety'];

  const convertChunk = (n) => {
    if (n < 20) return ones[n];
    if (n < 100) return tens[Math.floor(n / 10)] + (n % 10 ? ' ' + ones[n % 10] : '');
    if (n < 1000) return ones[Math.floor(n / 100)] + ' hundred' + (n % 100 ? ' and ' + convertChunk(n % 100) : '');
    return '';
  };

  if (num === 0) return 'zero';

  let result = '';
  const crore = Math.floor(num / 10000000);
  if (crore) result += convertChunk(crore) + ' crore ';
  num %= 10000000;

  const lakh = Math.floor(num / 100000);
  if (lakh) result += convertChunk(lakh) + ' lakh ';
  num %= 100000;

  const thousand = Math.floor(num / 1000);
  if (thousand) result += convertChunk(thousand) + ' thousand ';
  num %= 1000;

  if (num) result += convertChunk(num);

  return result.trim();
};

// CSS for invoice
const invoiceCss = `
  @page { 
    size: A4; 
    margin: 10mm;
  }
  body { 
    font-family: "Times New Roman", Times, serif; 
    margin: 0; 
    padding: 0; 
    -webkit-print-color-adjust: exact !important;
    print-color-adjust: exact !important;
    line-height: 1.5 !important;
  }
  .invoice-container { 
    width: 210mm; 
    min-height: 277mm; 
    margin: 0 auto; 
    padding: 10mm; 
    box-sizing: border-box; 
    position: relative;
    background: white;
    display: flex;
    flex-direction: column;
    justify-content: flex-start;
    page-break-after: always !important;
    page-break-inside: avoid !important;
  }
  .header { 
    text-align: center; 
    margin-bottom: 2mm; 
    border-bottom: 1px solid #000; 
    padding-bottom: 2mm; 
  }
  .company-name { 
    font-size: 18pt; 
    font-weight: bold; 
    margin-bottom: 1mm; 
  }
  .company-desc { 
    font-size: 12pt; 
    margin-bottom: 1mm; 
  }
  .company-address { 
    font-size: 10pt; 
    line-height: 1.3;
  }
  .invoice-title { 
    font-size: 14pt; 
    font-weight: bold; 
    margin: 2mm 0; 
    text-decoration: underline; 
    text-align: center; 
  }
  .bill-no {
    font-weight: bold;
    display: flex;
    justify-content: space-between;
    margin-bottom: 2mm;
    font-size: 11pt;
  }
  .bill-info { 
    display: flex; 
    align-items: center;
  }
  .address-box { 
    display: flex; 
    border: 1px solid #000; 
    margin-bottom: 3mm;
  }
  .address-section { 
    flex: 1; 
    padding: 2mm; 
    font-size: 10pt; 
    line-height: 1.4;
  }
  .address-section:first-child { 
    border-right: 1px solid #000; 
  }
  .address-section p { margin: 1mm 0; }
  .work-details { 
    font-size: 10pt; 
    margin-bottom: 3mm; 
    line-height: 1.4;
  }
  .work-details p { margin: 1mm 0; }
  table { 
    width: 100% !important; 
    border-collapse: collapse !important; 
    font-size: 10pt; 
    margin-bottom: 0;
    table-layout: fixed !important;
  }
  th, td { 
    border: 1px solid #000 !important; 
    padding: 1.5mm !important; 
    text-align: center !important; 
    word-wrap: break-word !important;
  }
  th { 
    background-color: #f2f2f2 !important; 
    font-weight: bold !important; 
  }
  .amount-container {
    border: 1px solid #000;
    border-top: none !important;
    margin: 0 !important;
    padding: 2mm !important;
  }
  .amount-row { 
    flex: 0 0 auto;
    margin: 1mm 5mm;
    text-align: right;
    color: #000 !important;
    font-size: 10pt;
  }
  .total-amount { 
    font-weight: bold; 
    font-size: 12pt; 
    padding-top: 1mm;
  }
  .amount-in-words { 
    font-size: 10pt; 
    padding: 2mm 0; 
    border-bottom: 1px dashed #000; 
    line-height: 1.4;
    margin-top: 2mm;
  }
  .amount-in-words p { margin: 1mm 0; }
  .signature { 
    text-align: end; 
    font-weight: bold; 
    font-size: 11pt;
    margin-top: 15mm;
  }
  .signature p { margin: 1mm 0; }
  .editable-input, .editable-select { 
    border: 1px solid #ccc; 
    background: #fff; 
    font-size: inherit; 
    font-family: inherit;
    padding: 1px;
    width: auto;
  }
  @media print { 
    body { 
      font-family: "Times New Roman", Times, serif !important; 
      background: white !important;
      margin: 0 !important;
      padding: 0 !important;
    }
    .no-print { display: none !important; } 
    .invoice-container { 
      width: 210mm !important; 
      min-height: 277mm !important;
      margin: 0 auto !important; 
      padding: 10mm !important;
      border: none !important;
      box-shadow: none !important;
      page-break-after: always !important;
    }
    .editable-input, .editable-select { 
      border: none !important; 
      background: none !important; 
      -webkit-appearance: none;
      -moz-appearance: none;
      appearance: none;
    }
  }
  @media screen { 
    .invoice-container { 
      margin: 10mm auto; 
      border: 1px solid #ccc;
      box-shadow: 0 0 10px rgba(0,0,0,0.1);
    }
  }
`;

// CSS for certificate and test results
const certificateCss = `
  @page { 
    size: A4; 
    margin: 10mm;
  }
  body { 
    font-family: "Times New Roman", Times, serif; 
    margin: 0; 
    padding: 0; 
    -webkit-print-color-adjust: exact !important;
    print-color-adjust: exact !important;
    line-height: 1.5 !important;
  }
  .certificate-container { 
    width: 210mm; 
    min-height: 277mm; 
    margin: 0 auto; 
    padding: 10mm; 
    box-sizing: border-box; 
    position: relative;
    background: white;
    display: flex;
    flex-direction: column;
    justify-content: flex-start;
    page-break-after: always !important;
    page-break-inside: avoid !important;
  }
  .certificate-header { text-align: center; margin-bottom: 5mm; }
  .certificate-header h1 { font-size: 20pt; text-decoration: underline; margin-bottom: 2mm; }
  .certificate-secondname { text-align: center; font-size: 14pt; margin-bottom: 3mm; font-weight: bold; }
  
  .company-header { margin-bottom: 5mm; text-align: center; border-bottom: 1px solid #000; padding-bottom: 3mm; }
  .company-header h2 { font-size: 16pt; margin-bottom: 2mm; font-weight: bold; }
  .company-header p { font-size: 11pt; margin: 0; line-height: 1.3; }
  
  .certificate-content { text-align: justify; font-size: 12pt; line-height: 1.6; flex-grow: 1; }
  .certificate-content ul { font-size: 11pt; line-height: 2; padding-left: 5mm; list-style-type: decimal; }
  .certificate-content li { margin-bottom: 2mm; display: flex; align-items: flex-start; }
  .certificate-content li input { font-size: 11pt; flex: 1; margin-left: 2mm; font-family: inherit; }
  
  .work-order-details { margin-top: 10mm; display: flex; justify-content: space-between; font-size: 11pt; font-weight: bold; }
  .work-order2 { font-size: 11pt; font-weight: bold; margin-top: 2mm; }
  
  .signature-section { margin-top: 15mm; display: flex; justify-content: space-between; text-align: center; font-size: 11pt; width: 100%; }
  .signature-line { border-top: 1px solid #000; width: 50mm; margin: 10mm auto 2mm auto; }
  
  .to-section { margin-bottom: 5mm; font-size: 11pt; line-height: 1.4; }
  .to-section p { margin: 1mm 0; }
  
  .subject-section { margin-bottom: 5mm; font-size: 11pt; line-height: 1.4; }
  .subject-section p { margin: 1mm 0; }
  
  .certificate-title { text-align: center; text-decoration: underline; margin: 5mm 0; font-size: 14pt; font-weight: bold; }
  
  .job-details table, .test-details table { width: 100% !important; border-collapse: collapse !important; margin: 5mm 0; font-size: 9pt; table-layout: fixed !important; }
  .job-details th, .job-details td, .test-details th, .test-details td { border: 1px solid #000 !important; padding: 1.5mm !important; text-align: center !important; word-wrap: break-word !important; }
  .job-details th, .test-details th { background-color: #f2f2f2 !important; font-weight: bold !important; }
  
  .guarantee-text { margin: 3mm 0; font-size: 11pt; line-height: 1.4; text-align: justify; }
  .guarantee-text p { margin: 2mm 0; }
  
  .note-section { margin-top: 5mm; font-size: 10pt; line-height: 1.4; }
  .note-section p { margin: 1mm 0; }
  
  .test-report { flex-grow: 1; display: flex; flex-direction: column; }
  .test-report-header { text-align: center; font-weight: bold; margin-bottom: 1mm; font-size: 12pt; }
  .test-details { font-size: 10pt; margin-bottom: 3mm; line-height: 1.4; }
  .test-details p { margin: 1mm 0; }
  
  .test-result-table { width: 100% !important; border-collapse: collapse !important; font-size: 8pt; margin-bottom: 2mm; table-layout: fixed !important; }
  .test-result-table th, .test-result-table td { border: 1px solid #000 !important; padding: 1mm !important; text-align: center !important; word-wrap: break-word !important; }
  .test-result-table th { background-color: #f2f2f2 !important; font-weight: bold !important; }
  .test-result-table td:has(.compact-test-table) { padding: 0 !important; }
  
  .compact-test-table { width: 100% !important; height: 100%; border-collapse: collapse !important; font-size: 7pt; margin: 0 !important; table-layout: fixed !important; }
  .compact-test-table td { border: 1px solid #ddd !important; padding: 0.5mm !important; text-align: center !important; }
  
  .test-result-footer { margin-top: 5mm; font-size: 9pt; line-height: 1.4; }
  
  .editable-input, .editable-select { border: 1px solid #ccc; background: #fff; font-size: inherit; font-family: inherit; padding: 1px; }
  .remove-btn { color: red; cursor: pointer; font-size: 12pt; margin-left: 2mm; font-weight: bold; text-decoration: none; }
  
  @media print {
    body { font-family: "Times New Roman", Times, serif !important; background: white !important; margin: 0 !important; padding: 0 !important; }
    .certificate-container { width: 210mm !important; min-height: 277mm !important; margin: 0 auto !important; padding: 10mm !important; border: none !important; box-shadow: none !important; page-break-after: always !important; }
    .editable-input, .editable-select { border: none !important; background: none !important; -webkit-appearance: none; -moz-appearance: none; appearance: none; }
    .remove-btn { display: none !important; }
  }
  @media screen { 
    .certificate-container { margin: 10mm auto; border: 1px solid #ccc; box-shadow: 0 0 10px rgba(0,0,0,0.1); }
  }
`;

// Modal CSS
const modalCss = `
  .modal-overlay { 
    position: fixed; 
    top: 0; 
    left: 0; 
    right: 0; 
    bottom: 0; 
    background: rgba(0, 0, 0, 0.5); 
    display: flex; 
    justify-content: center; 
    align-items: center; 
    z-index: 1000; 
  }
  .modal-content { 
    background: white; 
    padding: 5mm; 
    border-radius: 4px; 
    width: 90%; 
    max-width: 80mm; 
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2); 
  }
  .modal-content h2 { 
    font-size: 12pt; 
    margin-bottom: 3mm; 
  }
  .modal-content input { 
    width: 100%; 
    padding: 2mm; 
    margin-bottom: 3mm; 
    border: 1px solid #ccc; 
    border-radius: 2px; 
    font-size: 10pt;
  }
  .modal-content .error { 
    color: red; 
    font-size: 9pt; 
    margin-bottom: 2mm; 
  }
  .modal-content button { 
    background: #2563eb; 
    color: white; 
    padding: 2mm 4mm; 
    border: none; 
    border-radius: 2px; 
    cursor: pointer; 
    font-size: 10pt;
  }
  .modal-content button:hover { 
    background: #1d4ed8; 
  }
`;

const BillPrintView = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const printRef = useRef();

  // State initialization
  const [data, setData] = useState([]);
  const [companyDetails, setCompanyDetails] = useState({
    name: '',
    address: '',
    gst: '',
    tender: '',
    email: ''
  });
  const [clientDetails, setClientDetails] = useState({
    name: '',
    email: '',
    gst: ''
  });
  const [kvaPrices, setKvaPrices] = useState({});
  const [loading, setLoading] = useState(true);
  const [billNumber, setBillNumber] = useState('');
  const [billDate, setBillDate] = useState(new Date().toISOString().split('T')[0]);
  const [empanelmentNo, setEmpanelmentNo] = useState('');
  const [showForm, setShowForm] = useState(true);
  const [moNumbers, setMoNumbers] = useState({});
  const [printMode, setPrintMode] = useState('all');
  const [testReports, setTestReports] = useState({});
  const [lastBillNumber, setLastBillNumber] = useState(0);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [workOrderNo, setWorkOrderNo] = useState('');
  const [workOrderDate, setWorkOrderDate] = useState(new Date().toISOString().split('T')[0]);
  const [showBillDateModal, setShowBillDateModal] = useState(false);
  const [modalError, setModalError] = useState('');
  const [certificateItems, setCertificateItems] = useState([
    'The Transformer has been duly repaired by the agency.',
    'The repair work adheres to the specified standards.',
    'The Transformer has been painted as per the required specifications.',
    'Transportation of the Transformer was handled by the Department.',
    'The oil was drained prior to sending it for repairs.',
    'Fresh or reclaimed oil has been provided by the agency.',
    'The repaired Transformer has been tested and found satisfactory.',
    'Work was carried out prior to the Work Order due to an emergency.',
    'This bill has not been recorded previously.',
    `The amount of the bill is Rs. {finalTotal}/- (excluding applicable taxes).`
  ]);

  // Check for mobile view
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Destructure location state
  const {
    company = '',
    division = 'Vita Division',
    ids = [],
    workOrderNo: initialWorkOrderNo = '',
    workOrderDate: initialWorkOrderDate = '',
    moNumbers: locationMoNumbers = {}
  } = location.state || {};

  const normalizedCompany = normalizeCompanyKey(company);
  const sanitizedCompany = firebaseCompanyKey(normalizedCompany);

  // Validate location state
  useEffect(() => {
    if (!location.state) {
      alert('Missing data. Please go back to Register and try again.');
      navigate('/register');
    } else {
      setMoNumbers(locationMoNumbers);
      setWorkOrderNo(initialWorkOrderNo);
      setWorkOrderDate(initialWorkOrderDate);
    }
  }, [location.state, navigate]);

  // Fetch data from Firebase including last bill number
  useEffect(() => {
    if (!sanitizedCompany || !division || ids.length === 0) return;

    const fetchData = async () => {
      setLoading(true);
      try {
       const [entrySnap, kvaSnap, compSnap, clientSnap, reportsSnap, billSummarySnap] = await Promise.all([
      get(ref(realtimeDb, `companies/${sanitizedCompany}/${division}/register`)),
      get(ref(realtimeDb, `companies/${sanitizedCompany}/${division}/kvaPrices`)),
      get(ref(realtimeDb, `companyDetails/${sanitizedCompany}`)),
      get(ref(realtimeDb, `companies/${sanitizedCompany}/${division}/info`)), // This is where client details are stored
      get(ref(realtimeDb, 'testReports')),
      get(ref(realtimeDb, `companies/${sanitizedCompany}/${division}/billSummary`))
    ]);

    const entryVal = entrySnap.val() || {};
    const kvaVal = kvaSnap.val() || {};
    const compVal = compSnap.val() || {};
    const clientVal = clientSnap.val() || {}; // This contains the client info from Firebase
    const reportsVal = reportsSnap.val() || {};
    const billSummaryVal = billSummarySnap.val() || {};

        // Set company and client details
          setCompanyDetails({
      name: normalizedCompany,
      address: compVal.address || 'PLOT NO. B.27, JATH MINI INDUSTRIAL AREA, JATH DIST. SANGLI-416404',
      gst: compVal.gst || 'N/A',
      tender: compVal.tender || 'N/A',
      email: compVal.email || 'N/A'
    });
        setEmpanelmentNo(compVal.empanelment || 'SE/OTR/100/LOI/68042');

        setClientDetails({
      name: 'The Executive', // Default value
      address: clientVal.address || 'Abhincshi Parisar', // From your image
      email: clientVal.email || 'shripailf3412@gmail.com', // From your image
      gst: clientVal.gst || '32462', // From your image
      tender: clientVal.tender || '42572' // From your image
    });

        // Determine the next bill number
        const existingBillNumbers = Object.keys(billSummaryVal).map(Number).filter(n => !isNaN(n));
        const nextBillNumber = existingBillNumbers.length > 0 ? Math.max(...existingBillNumbers) + 1 : 1;
        setLastBillNumber(nextBillNumber);
        setBillNumber(nextBillNumber.toString());

        // Fetch only selected jobs
        const selectedData = Object.entries(entryVal)
          .filter(([id]) => ids.includes(id))
          .map(([id, item]) => {
            const kvaType = `${item.kva} KVA ${item.type}`;
            const price = parseFloat(kvaVal[kvaType] || 0);
            return {
              id,
              jobNo: item.jobNo || 'N/A',
              make: item.make || 'N/A',
              makeSrNo: item.makeSrNo || 'N/A',
              kva: item.kva || 'N/A',
              type: item.type || 'N/A',
              oilCap: item.oilCap || '0.00',
              finalChallanNo: item.finalChallanNo || 'N/A',
              finalVehicleNo: item.finalVehicleNo || 'N/A',
              amount: price,
              moNumber: moNumbers[id] || item.moNumber || 'N/A',
              materialType: item.materialType || 'ALU',
              date: item.date || 'N/A',
              dispatchDate: item.dispatchDate || 'N/A',
              challanNo: item.challanNo || 'N/A'
            };
          });

        setData(selectedData);
        setKvaPrices(kvaVal);
        setTestReports(reportsVal);
      } catch (err) {
        console.error(err);
        alert('Failed to load data.');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [sanitizedCompany, division, ids, moNumbers]);

  // Calculate bill summary
  const calculateBill = (data) => {
    const grossTotal = data.reduce((sum, item) => sum + (item.amount || 0), 0);
    const cgst = +(grossTotal * 0.09).toFixed(2);
    const sgst = +(grossTotal * 0.09).toFixed(2);
    const finalTotal = +(grossTotal + cgst + sgst).toFixed(2);
    return { grossTotal, cgst, sgst, finalTotal };
  };

  // Handle bill date submission
  const handleBillDateSubmit = (e) => {
    e.preventDefault();
    if (!billDate) {
      setModalError('Please select a valid bill date.');
      return;
    }
    setModalError('');
    setShowBillDateModal(false);
    setShowForm(false);
  };

  // Handle test report input change
  const handleTestReportInputChange = (recordIndex, section, row, col, value) => {
    setTestReports(prev => {
      const updatedReports = { ...prev };
      const kva = kvaOptions.includes(parseInt(data[recordIndex].kva)) ? data[recordIndex].kva : null;
      if (!kva) return prev;
      const key = `${sanitizedCompany.replace(/\s+/g, '_').toUpperCase()}_${kva}`;
      const report = updatedReports[key] || {
        voltageRatio: Array(6).fill().map(() => Array(3).fill('')),
        megger: Array(3).fill().map(() => Array(3).fill('')),
        shortCircuit: Array(6).fill().map(() => Array(3).fill('')),
        noOfCharging: Array(4).fill().map(() => Array(3).fill(''))
      };
      report[section][row][col] = value;
      updatedReports[key] = report;
      return updatedReports;
    });
  };

  // Generate invoice HTML
  const generateInvoice = () => {
    const billSummary = calculateBill(data);

    return `
      <div class="invoice-container" style="page-break-after: always;">
        <div class="header">
          <div class="company-name">${companyDetails.name}</div>
          <div class="company-desc">Transformer Service</div>
          <div class="company-address">Address: ${companyDetails.address}</div>
        </div>

        <div class="invoice-title">TAX INVOICE</div>

        <div class="bill-no">
          <div class="bill-info">
            <div class="bill-no">Bill No: </div>
            <div><input class="editable-input" value="${billNumber}" onChange="this.value = event.target.value; document.dispatchEvent(new CustomEvent('updateBillNumber', { detail: this.value }))" /></div>
          </div>
          <div>Bill Date: ${formatDate(billDate)}</div>
        </div>

        <div class="address-box">
          <div class="address-section">
            <p><strong>Name:</strong> ${companyDetails.name}</p>
            <p><strong>Address:</strong> ${companyDetails.address}</p>
            <p><strong>Email:</strong> ${companyDetails.email}</p>
            <p><strong>GST No:</strong> ${companyDetails.gst}</p>
          </div>
          <div class="address-section">
           <p><strong>Name:</strong> ${clientDetails.name}</p>
          <p><strong>Address:</strong> ${clientDetails.address}</p>
          <p><strong>Email:</strong> ${clientDetails.email}</p>
          <p><strong>GST No:</strong> ${clientDetails.gst}</p>
          </div>
        </div>

        <div class="work-details">
          <p><strong>Name of work:</strong> Repairing of DT_UPTO_100 KVA Distribution (${division})</p>
          <p><strong>Ref:</strong></p>
          <p>1. Tender Ref: ${companyDetails.tender}</p>
          <p>2. Letter of Empanelment No. ${empanelmentNo}</p>
          <p>3. EE/DTRR/WO/${workOrderNo}/${formatDate(workOrderDate)}</p>
        </div>

        <table>
          <thead>
            <tr>
              <th style="width: 5%;">Sr No</th>
              <th style="width: 7%;">HSN Code</th>
              <th style="width: 10%;">Description</th>
              <th style="width: 8%;">Job No</th>
              <th style="width: 8%;">Make</th>
              <th style="width: 8%;">Make Sr No</th>
              <th style="width: 6%;">KVA</th>
              <th style="width: 8%;">Material</th>
              <th style="width: 8%;">Amount (₹)</th>
            </tr>
          </thead>
          <tbody>
            ${data.map((item, idx) => `
              <tr>
                <td>${idx + 1}</td>
                <td>998719</td>
                <td>Repair of Transformer</td>
                <td>${item.jobNo}</td>
                <td>${item.make}</td>
                <td>${item.makeSrNo}</td>
                <td>${item.kva}</td>
                <td>
                  <select class="editable-select" onchange="this.value = event.target.value; document.dispatchEvent(new CustomEvent('updateMaterialType', { detail: { index: ${idx}, value: this.value } }))">
                    <option value="ALU" ${item.materialType === 'ALU' ? 'selected' : ''}>ALU</option>
                    <option value="COPPER" ${item.materialType === 'COPPER' ? 'selected' : ''}>COPPER</option>
                  </select>
                </td>
                <td>${(item.amount || 0).toFixed(2)}</td>
              </tr>
            `).join('')}
          </tbody>
        </table>

        <div class="amount-container">
          <div class="amount-row">Gross Bill: ₹${billSummary.grossTotal.toFixed(2)}</div>
          <div class="amount-row">Add CGST 9%: ₹${billSummary.cgst.toFixed(2)}</div>
          <div class="amount-row">Add SGST 9%: ₹${billSummary.sgst.toFixed(2)}</div>
          <div class="amount-row total-amount">Total Amount: ₹${billSummary.finalTotal.toFixed(2)}</div>
        </div>

        <div class="amount-in-words">
          <p><strong>Total Amount (In Rupees):</strong> ₹${billSummary.finalTotal.toFixed(2)}</p>
          <p><strong>Total Amount (In Words):</strong> ${numberToWords(Math.round(billSummary.finalTotal))} rupees only</p>
        </div>

        <div class="signature">
          <p>${companyDetails.name}</p>
          <p>Proprietor</p>
        </div>
      </div>
    `;
  };

  // Generate certificate HTML
  const generateCertificate = () => {
    const billSummary = calculateBill(data);
    const workStartDate = data[0]?.date || 'N/A';
    const workEndDate = data[0]?.dispatchDate || 'N/A';

    return `
      <div class="certificate-container" style="page-break-after: always;">
        <div class="certificate-header">
          <h1>CERTIFICATE</h1>
        </div>

        <div class="certificate-content">
          <p class="certificate-secondname"><strong>Certified that:</strong></p>
          <ul>
            ${certificateItems.map((item, idx) => `
              <li>
                <input
                  class="editable-input"
                  value="${item.includes('{finalTotal}') ? item.replace('{finalTotal}', billSummary.finalTotal.toFixed(2)) : item}"
                  style="width: 90%"
                  onChange="this.value = event.target.value; document.dispatchEvent(new CustomEvent('updateCertificateItem', { detail: { index: ${idx}, value: this.value } }))"
                />
                <span class="remove-btn" onclick="document.dispatchEvent(new CustomEvent('removeCertificateItem', { detail: ${idx} }))">X</span>
              </li>
            `).join('')}
          </ul>

          <div class="work-order-details">
            <p><strong>Work Start Date:</strong> ${formatDate(workStartDate)}</p>
            <p><strong>Work End Date:</strong> ${formatDate(workEndDate)}</p>
          </div>
          <div class="work-order2">
            <p><strong>Work Order No:</strong> ${workOrderNo}</p>
          </div>

          <div class="signature-section">
            <div>
              <p><strong>Additional Executive Engineer</strong></p>
              <p>Division: ${division}</p>
              <p class="signature-line"></p>
            </div>
            <div>
              <p><strong>Assistant Engineer (F.U), M.S.E.D.C. LTD</strong></p>
              <p>Division: ${division}</p>
              <p class="signature-line"></p>
            </div>
          </div>
        </div>
      </div>
    `;
  };

  // Generate performance guarantee HTML
  const generatePerformanceGuarantee = () => {
    return `
      <div class="certificate-container" style="page-break-after: always;">
        <div class="certificate-header">
          <div class="company-header">
            <h2>${companyDetails.name}</h2>
            <p>Address: ${companyDetails.address}</p>
          </div>
          <h3>Repairs of Transformers</h3>
        </div>

        <div class="to-section">
          <p>To,</p>
          <p>${clientDetails.name}</p>
          <p>M.S.E.D.C.Ltd. O&M Division</p>
          <p>${division}</p>
        </div>

        <div class="subject-section">
          <p><strong>Subject:</strong> Guarantee Certificate</p>
          <p><strong>Name of Work:</strong> Repairing of DT_UPTO_100KVA Distribution Transformer Under ${division} DIVISION</p>
          <p><strong>Ref:</strong></p>
          <p>1. Tender Ref. No: ${companyDetails.tender}</p>
          <p>2. Letter of Empanelment No. ${empanelmentNo}</p>
          <p>3. EE/OTRR/WO/${workOrderNo} DATE: ${workOrderDate}</p>
        </div>

        <div class="certificate-title">
          <h2>Performance Guarantee Certificate</h2>
        </div>

        <div class="job-details">
          <p>Job details:</p>
          <table>
            <thead>
              <tr>
                <th>SR.NO</th>
                <th>JOB.NO</th>
                <th>MAKE</th>
                <th>MAKE SR.NO</th>
                <th>KVA</th>
                <th>OIL.CAP</th>
                <th>M.S.E.D.C.L.</th>
                <th>DATE</th>
                <th>Challan No.</th>
                <th>DATE</th>
                <th>TYPE</th>
              </tr>
            </thead>
            <tbody>
              ${data.map((item, idx) => `
                <tr>
                  <td>${idx + 1}</td>
                  <td>${item.jobNo}</td>
                  <td>${item.make}</td>
                  <td>${item.makeSrNo}</td>
                  <td>${item.kva}</td>
                  <td>${item.oilCap}</td>
                  <td>${item.challanNo}</td>
                  <td>${formatDate(item.date)}</td>
                  <td>${item.finalChallanNo}</td>
                  <td>${formatDate(item.dispatchDate)}</td>
                  <td>${item.type}</td>
                </tr>
              `).join('')}
            </tbody>
          </table>
        </div>

        <div class="guarantee-text">
          <p>We hereby certify that the repaired transformer is guaranteed for trouble-free service for a period of 12 months from the date of commissioning or 18 months from the date of dispatch, whichever is earlier.</p>
          <p>We further certify that all damaged parts have been replaced with original, genuine parts of reputed standard make of the best quality as per IS standards.</p>
        </div>

        <div class="signature-section" style="justify-content: flex-end;">
          <div>
            <p><strong>For M/s ${companyDetails.name}</strong></p>
            <p>Authorised Signatory</p>
            <p class="signature-line"></p>
          </div>
        </div>

        <div class="note-section">
          <p><strong>N.B.</strong></p>
          <p>Your transformer commissioning report 'Annexure-d' must reach us with an acknowledgment copy within two weeks from the date of commissioning.</p>
        </div>
      </div>
    `;
  };

  // Generate test results
  const generateTestResults = () => {
    if (!testReports || !sanitizedCompany || !data) {
      return '<div>Loading test reports...</div>';
    }

    const defaultTestReport = {
      voltageRatio: [['', '', ''], ['', '', ''], ['', '', ''], ['', '', ''], ['', '', ''], ['', '', '']],
      megger: [['', '', ''], ['', '', ''], ['', '', '']],
      shortCircuit: [['', '', ''], ['', '', ''], ['', '', ''], ['', '', ''], ['', '', ''], ['', '', '']],
      noOfCharging: [['', '', ''], ['', '', ''], ['', '', ''], ['', '', '']]
    };

    const testRecords = data.map((item, idx) => {
      const kva = kvaOptions.includes(parseInt(item.kva)) ? item.kva : '-';
      const key = kva !== '-' ? `${sanitizedCompany.replace(/\s+/g, '_').toUpperCase()}_${kva}` : null;
      const report = key ? (testReports[key] || defaultTestReport) : defaultTestReport;

      const fillEmpty = (arr, rows, cols) => {
        const result = [];
        for (let i = 0; i < rows; i++) {
          const row = arr[i] ? [...arr[i]] : Array(cols).fill('');
          while (row.length < cols) row.push('');
          result.push(row);
        }
        while (result.length < rows) result.push(Array(cols).fill(''));
        return result;
      };

      return {
        srNo: idx + 1,
        moNumber: item.moNumber || '-',
        jobNo: item.jobNo || '-',
        kva: kva,
        make: item.make || '-',
        makeSrNo: item.makeSrNo || '-',
        type: item.type || '-',
        oilCap: item.oilCap || '-',
        finalChallanNo: item.finalChallanNo || '-',
        finalVehicleNo: item.finalVehicleNo || '-',
        voltageRatio: fillEmpty(report.voltageRatio || [], 6, 3),
        megger: fillEmpty(report.megger || [], 3, 3),
        shortCircuit: fillEmpty(report.shortCircuit || [], 6, 3),
        noOfCharging: fillEmpty(report.noOfCharging || [], 4, 3)
      };
    });

    return `
      <div class="certificate-container">
        <div class="test-report">
          <h2 class="test-result-header">MAHARASHTRA STATE ELECTRICITY DISTRIBUTION CO.LTD.</h2>
          <p class="test-result-header">Repaired Transformer Test Results</p>
          <div class="test-details">
            <p><strong>Name Of Repairer:</strong> ${companyDetails.name}</p>
            <p><strong>Address:</strong> ${companyDetails.address}</p>
            <p><strong>Name Of Consumer:</strong> M.S.E.D.C.Ltd. (O&M) ${division} Division</p>
            <p><strong>Date:</strong> ${new Date().toLocaleDateString('en-GB')}</p>
          </div>

          <div class="test-report-container">
            <table class="test-result-table">
              <thead>
                <tr>
                  <th>Sr. No.</th>
                  ${testRecords.map((record, idx) => `<th key="${idx}">${record.srNo}</th>`).join('')}
                </tr>
              </thead>
              <tbody>
                ${['moNumber', 'jobNo', 'kva', 'make', 'makeSrNo', 'type', 'oilCap', 'finalChallanNo', 'finalVehicleNo'].map(field => `
                  <tr key="${field}">
                    <td>${field.toUpperCase().replace('FINALCHALLANNO', 'FINAL CHALLAN NO').replace('FINALVEHICLENO', 'FINAL VEHICLE NO').replace('MONUMBER', 'MO NUMBER')}</td>
                    ${testRecords.map((record, idx) => `
                      <td key="${idx}">${record[field]}</td>
                    `).join('')}
                  </tr>
                `).join('')}
                ${[
                  { label: 'Voltage Ratio', key: 'voltageRatio', rows: 6, cols: 3 },
                  { label: 'Megger', key: 'megger', rows: 3, cols: 3 },
                  { label: 'Short Circuit', key: 'shortCircuit', rows: 6, cols: 3 },
                  { label: 'No of Charging', key: 'noOfCharging', rows: 4, cols: 3 }
                ].map(section => `
                  <tr key="${section.key}">
                    <td>${section.label}</td>
                    ${testRecords.map((record, idx) => `
                      <td key="${idx}">
                        <table class="compact-test-table">
                          ${record[section.key].map((row, rowIdx) => `
                            <tr key="${rowIdx}">
                              ${row.map((cell, cellIdx) => `
                                <td key="${cellIdx}">
                                  <input
                                    class="editable-input"
                                    value="${cell}"
                                    onChange="document.dispatchEvent(new CustomEvent('updateTestReport', { detail: { recordIndex: ${idx}, section: '${section.key}', row: ${rowIdx}, col: ${cellIdx}, value: event.target.value } }))"
                                  />
                                </td>
                              `).join('')}
                            </tr>
                          `).join('')}
                        </table>
                      </td>
                    `).join('')}
                  </tr>
                `).join('')}
              </tbody>
            </table>
          </div>

          <div class="test-result-footer">
            <p><strong>GUARANTEE CERTIFICATE:</strong></p>
            <p>First/Second Repaired Transformer/Job 18/12 Months from the date of commissioning and 18/12 Months from dispatch whichever is earlier.</p>
          </div>

          <div class="signature-section">
            <div>
              <p><strong>Additional Executive Engineer.</strong></p>
              <p>Division: ${division}</p>
              <p class="signature-line"></p>
            </div>
            <div>
              <p><strong>For M/s ${companyDetails.name}</strong></p>
              <p>JATH</p>
              <p class="signature-line"></p>
            </div>
          </div>
        </div>
      </div>
    `;
  };

  // Handle print functionality
  const handlePrint = (mode) => {
    if (!billNumber) {
      alert('Please enter a bill number');
      return;
    }
    if (!billDate) {
      alert('Please select a bill date.');
      setShowBillDateModal(true);
      return;
    }

    const updates = {};
    const billSummary = calculateBill(data);
    const billPath = `companies/${sanitizedCompany}/${division}/billSummary/${billNumber}`;

    // Update register details
    data.forEach(item => {
      updates[`companies/${sanitizedCompany}/${division}/register/${item.id}/moNumber`] = item.moNumber;
      updates[`companies/${sanitizedCompany}/${division}/register/${item.id}/workOrderNo`] = workOrderNo;
      updates[`companies/${sanitizedCompany}/${division}/register/${item.id}/workOrderDate`] = workOrderDate;
      updates[`companies/${sanitizedCompany}/${division}/register/${item.id}/workBillNo`] = billNumber;
      updates[`companies/${sanitizedCompany}/${division}/register/${item.id}/billDate`] = billDate;
      updates[`companies/${sanitizedCompany}/${division}/register/${item.id}/materialType`] = item.materialType;
    });

    // Update test reports
    data.forEach(item => {
      const kva = kvaOptions.includes(parseInt(item.kva)) ? item.kva : null;
      if (kva) {
        const key = `${sanitizedCompany.replace(/\s+/g, '_').toUpperCase()}_${kva}`;
        updates[`testReports/${key}`] = testReports[key] || {};
      }
    });

    // Store bill summary data
    updates[billPath] = {
      billNumber,
      billDate,
      grossTotal: billSummary.grossTotal,
      cgst: billSummary.cgst,
      sgst: billSummary.sgst,
      finalTotal: billSummary.finalTotal,
      company: normalizedCompany,
      division,
      workOrderNo,
      workOrderDate,
      items: data.map(item => ({
        id: item.id,
        jobNo: item.jobNo,
        make: item.make,
        makeSrNo: item.makeSrNo,
        kva: item.kva,
        type: item.type,
        oilCap: item.oilCap,
        finalChallanNo: item.finalChallanNo,
        finalVehicleNo: item.finalVehicleNo,
        amount: item.amount,
        moNumber: item.moNumber,
        materialType: item.materialType
      })),
      clientDetails,
      companyDetails,
      address: companyDetails.address
    };

    updates[`companyDetails/${sanitizedCompany}/empanelment`] = empanelmentNo;

    update(ref(realtimeDb), updates)
      .then(() => {
        const printWindow = window.open('', '_blank');
        let printContent = '';

        if (mode === 'all') {
          printContent = `
            ${generateInvoice()}
            <div style="page-break-before: always;"></div>
            ${generateCertificate()}
            <div style="page-break-before: always;"></div>
            ${generatePerformanceGuarantee()}
            <div style="page-break-before: always;"></div>
            ${generateTestResults()}
          `;
        } else {
          switch (mode) {
            case 'invoice':
              printContent = generateInvoice();
              break;
            case 'certificate':
              printContent = generateCertificate();
              break;
            case 'performance':
              printContent = generatePerformanceGuarantee();
              break;
            case 'test':
              printContent = generateTestResults();
              break;
            default:
              printContent = generateInvoice();
          }
        }

        printWindow.document.write(`
          <!DOCTYPE html>
          <html>
            <head>
              <title>${getDocumentTitle(mode)}</title>
              <style>
                ${invoiceCss}
                ${certificateCss}
                ${modalCss}
                
                /* Additional print-specific fixes */
                @page {
                  size: A4;
                  margin: 10mm;
                }
                
                body {
                  font-family: "Times New Roman", Times, serif !important;
                  line-height: 1.5 !important;
                }
                
                .invoice-container, .certificate-container {
                  zoom: 100% !important;
                }
              </style>
            </head>
            <body>
              ${printContent}
              <script>
                window.onload = function() {
                  setTimeout(function() {
                    window.print();
                    setTimeout(function() { window.close(); }, 500);
                  }, 200);
                }
              </script>
            </body>
          </html>
        `);
        printWindow.document.close();
      })
      .catch(error => {
        console.error('Error updating data:', error);
        alert('Failed to update job details.');
      });
  };

  // Get document title for print window
  const getDocumentTitle = (mode) => {
    switch (mode) {
      case 'invoice': return 'Tax Invoice';
      case 'certificate': return 'Certificate';
      case 'performance': return 'Performance Guarantee Certificate';
      case 'test': return 'Test Results';
      case 'all': return 'Complete Documents';
      default: return 'Document';
    }
  };

  // Handle form submission
  const handleSubmit = (e) => {
    e.preventDefault();
    handleBillDateSubmit(e);
  };


  // Save bill without printing
  const handleSaveBillOnly = async () => {
    if (!billNumber) {
      alert('Please enter a bill number');
      return;
    }
    if (!billDate) {
      alert('Please select a bill date.');
      setShowBillDateModal(true);
      return;
    }

    const updates = {};
    const billSummary = calculateBill(data);
    const billPath = `companies/${sanitizedCompany}/${division}/billSummary/${billNumber}`;

    // Update register details
    data.forEach(item => {
      updates[`companies/${sanitizedCompany}/${division}/register/${item.id}/moNumber`] = item.moNumber;
      updates[`companies/${sanitizedCompany}/${division}/register/${item.id}/workOrderNo`] = workOrderNo;
      updates[`companies/${sanitizedCompany}/${division}/register/${item.id}/workOrderDate`] = workOrderDate;
      updates[`companies/${sanitizedCompany}/${division}/register/${item.id}/workBillNo`] = billNumber;
      updates[`companies/${sanitizedCompany}/${division}/register/${item.id}/billDate`] = billDate;
      updates[`companies/${sanitizedCompany}/${division}/register/${item.id}/materialType`] = item.materialType;
    });

    // Update test reports
    data.forEach(item => {
      const kva = kvaOptions.includes(parseInt(item.kva)) ? item.kva : null;
      if (kva) {
        const key = `${sanitizedCompany.replace(/\s+/g, '_').toUpperCase()}_${kva}`;
        updates[`testReports/${key}`] = testReports[key] || {};
      }
    });

    // Store bill summary data
    updates[billPath] = {
      billNumber,
      billDate,
      grossTotal: billSummary.grossTotal,
      cgst: billSummary.cgst,
      sgst: billSummary.sgst,
      finalTotal: billSummary.finalTotal,
      company: normalizedCompany,
      division,
      workOrderNo,
      workOrderDate,
      items: data.map(item => ({
        id: item.id,
        jobNo: item.jobNo,
        make: item.make,
        makeSrNo: item.makeSrNo,
        kva: item.kva,
        type: item.type,
        oilCap: item.oilCap,
        finalChallanNo: item.finalChallanNo,
        finalVehicleNo: item.finalVehicleNo,
        amount: item.amount,
        moNumber: item.moNumber,
        materialType: item.materialType
      })),
      clientDetails,
      companyDetails,
      address: companyDetails.address
    };

    updates[`companyDetails/${sanitizedCompany}/empanelment`] = empanelmentNo;

    try {
      await update(ref(realtimeDb), updates);
      alert('Bill details saved successfully!');
    } catch (error) {
      console.error('Error saving bill:', error);
      alert('Failed to save bill details.');
    }
  };

  // Handle print button clicks
  const handlePrintButtonClick = (mode) => {
    setPrintMode(mode);
    handlePrint(mode);
  };

  // Handle bill number change
  const handleBillNumberChange = (value) => {
    setBillNumber(value);
  };

  // Handle certificate item change
  const handleCertificateItemChange = (index, value) => {
    setCertificateItems(prev => {
      const updatedItems = [...prev];
      updatedItems[index] = value;
      return updatedItems;
    });
  };

  // Handle material type change
  const handleMaterialTypeChange = (index, value) => {
    setData(prev => {
      const updatedData = [...prev];
      updatedData[index] = { ...updatedData[index], materialType: value };
      return updatedData;
    });
  };

  // Handle remove certificate item
  const handleRemoveCertificateItem = (index) => {
    setCertificateItems(prev => prev.filter((_, i) => i !== index));
  };

  // Add event listeners for dynamic updates
  useEffect(() => {
    const handleBillNumberUpdate = (e) => {
      handleBillNumberChange(e.detail);
    };

    const handleCertificateItemUpdate = (e) => {
      const { index, value } = e.detail;
      handleCertificateItemChange(index, value);
    };

    const handleTestReportUpdate = (e) => {
      const { recordIndex, section, row, col, value } = e.detail;
      handleTestReportInputChange(recordIndex, section, row, col, value);
    };

    const handleMaterialTypeUpdate = (e) => {
      const { index, value } = e.detail;
      handleMaterialTypeChange(index, value);
    };

    const handleRemoveCertificate = (e) => {
      handleRemoveCertificateItem(e.detail);
    };

    document.addEventListener('updateBillNumber', handleBillNumberUpdate);
    document.addEventListener('updateCertificateItem', handleCertificateItemUpdate);
    document.addEventListener('updateTestReport', handleTestReportUpdate);
    document.addEventListener('updateMaterialType', handleMaterialTypeUpdate);
    document.addEventListener('removeCertificateItem', handleRemoveCertificate);

    return () => {
      document.removeEventListener('updateBillNumber', handleBillNumberUpdate);
      document.removeEventListener('updateCertificateItem', handleCertificateItemUpdate);
      document.removeEventListener('updateTestReport', handleTestReportUpdate);
      document.removeEventListener('updateMaterialType', handleMaterialTypeUpdate);
      document.removeEventListener('removeCertificateItem', handleRemoveCertificate);
    };
  }, [data, sanitizedCompany]);

  // Loading state
  if (loading) {
    return (
      <div className="flex justify-center items-center h-screen">
        <div className="animate-spin h-10 w-10 border-4 border-blue-500 border-t-transparent rounded-full"></div>
      </div>
    );
  }

  // Form for entering bill date only
  if (showForm) {
    return (
      <div className="p-4 sm:p-6 max-w-md mx-auto bg-white shadow-md rounded-lg">
        <h2 className="text-xl font-bold mb-4">Edit Bill Details</h2>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium mb-1">Bill Number</label>
            <input
              type="text"
              value={billNumber}
              onChange={(e) => setBillNumber(e.target.value)}
              className="w-full p-2 border rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
              required
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Bill Date</label>
            <input
              type="date"
              value={billDate}
              onChange={(e) => setBillDate(e.target.value)}
              className="w-full p-2 border rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
              required
            />
            {modalError && <p className="text-red-500 text-sm">{modalError}</p>}
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Letter of Empanelment No.</label>
            <input
              type="text"
              value={empanelmentNo}
              onChange={(e) => setEmpanelmentNo(e.target.value)}
              className="w-full p-2 border rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <div className="flex flex-col sm:flex-row justify-between gap-2">
            <button
              type="button"
              onClick={() => navigate(-1)}
              className="bg-gray-300 px-4 py-2 rounded flex items-center justify-center gap-2 hover:bg-gray-400 transition"
            >
              <FaArrowLeft /> Back
            </button>
            <button
              type="submit"
              className="bg-blue-600 text-white px-4 py-2 rounded flex items-center justify-center gap-2 hover:bg-blue-700 transition"
            >
              Continue <FaPrint className="transform rotate-180" />
            </button>
          </div>
        </form>
      </div>
    );
  }

  // Main render with documents
  return (
    <div className="p-4 sm:p-6 bg-gray-100 min-h-screen" ref={printRef}>
      <style>{invoiceCss}{certificateCss}{modalCss}</style>

      {/* Mobile header */}
      {isMobile && (
        <div className="bg-blue-600 text-white p-3 mb-4 rounded-lg shadow-md no-print">
          <div className="flex justify-between items-center">
            <button
              onClick={() => navigate(-1)}
              className="p-2 rounded-full hover:bg-blue-700"
            >
              <FaArrowLeft />
            </button>
            <h2 className="text-lg font-bold">Bill Documents</h2>
            <button
              onClick={() => setShowForm(true)}
              className="p-2 rounded-full hover:bg-blue-700"
            >
              <FaEdit />
            </button>
          </div>
        </div>
      )}

      {/* Desktop controls */}
      {!isMobile && (
        <div className="flex flex-wrap justify-between items-center mb-6 no-print gap-2">
          <div className="flex items-center gap-2">
            <button
              onClick={() => navigate(-1)}
              className="bg-gray-500 text-white px-4 py-2 rounded flex items-center gap-2 hover:bg-gray-600 transition"
            >
              <FaArrowLeft /> Back
            </button>
            <button
              onClick={() => setShowForm(true)}
              className="bg-gray-300 px-4 py-2 rounded flex items-center gap-2 hover:bg-gray-400 transition"
            >
              <FaEdit /> Edit Bill Details
            </button>
          </div>
          <div className="flex flex-wrap gap-2">
            <button
              onClick={() => handlePrintButtonClick('invoice')}
              className="bg-blue-600 text-white px-4 py-2 rounded flex items-center gap-2 hover:bg-blue-700 transition"
            >
              <FaFileAlt /> Print Invoice
            </button>
            <button
              onClick={() => handlePrintButtonClick('certificate')}
              className="bg-green-600 text-white px-4 py-2 rounded flex items-center gap-2 hover:bg-green-700 transition"
            >
              <FaFileAlt /> Print Certificate
            </button>
            <button
              onClick={() => handlePrintButtonClick('performance')}
              className="bg-purple-600 text-white px-4 py-2 rounded flex items-center gap-2 hover:bg-purple-700 transition"
            >
              <FaFileContract /> Print Performance Guarantee
            </button>
            <button
              onClick={() => handlePrintButtonClick('test')}
              className="bg-orange-600 text-white px-4 py-2 rounded flex items-center gap-2 hover:bg-orange-700 transition"
            >
              <FaFileSignature /> Print Test Results
            </button>
            <button
              onClick={() => handlePrintButtonClick('all')}
              className="bg-red-600 text-white px-4 py-2 rounded flex items-center gap-2 hover:bg-red-700 transition"
            >
              <FaPrint /> Print All
            </button>
            <button
              onClick={handleSaveBillOnly}
              className="bg-teal-600 text-white px-4 py-2 rounded flex items-center gap-2 hover:bg-teal-700 transition"
            >
              <FaSave /> Save Bill
            </button>

          </div>
        </div>
      )}

      {/* Mobile print buttons */}
      {isMobile && (
        <div className="grid grid-cols-2 gap-2 mb-4 no-print">
          <button
            onClick={() => handlePrintButtonClick('invoice')}
            className="bg-blue-600 text-white p-2 rounded flex flex-col items-center gap-1 text-sm"
          >
            <FaFileAlt size={16} />
            Invoice
          </button>
          <button
            onClick={() => handlePrintButtonClick('certificate')}
            className="bg-green-600 text-white p-2 rounded flex flex-col items-center gap-1 text-sm"
          >
            <FaFileAlt size={16} />
            Certificate
          </button>
          <button
            onClick={() => handlePrintButtonClick('performance')}
            className="bg-purple-600 text-white p-2 rounded flex flex-col items-center gap-1 text-sm"
          >
            <FaFileContract size={16} />
            Guarantee
          </button>
          <button
            onClick={() => handlePrintButtonClick('test')}
            className="bg-orange-600 text-white p-2 rounded flex flex-col items-center gap-1 text-sm"
          >
            <FaFileSignature size={16} />
            Test Results
          </button>
          <button
            onClick={() => handlePrintButtonClick('all')}
            className="bg-red-600 text-white p-2 rounded flex flex-col items-center gap-1 text-sm col-span-2"
          >
            <FaPrint size={16} />
            Print All Documents
          </button>
        </div>
      )}

      {/* Bill Date Modal */}
      {showBillDateModal && (
        <div className="modal-overlay">
          <div className="modal-content">
            <h2>Enter Bill Date</h2>
            {modalError && <p className="error">{modalError}</p>}
            <form onSubmit={handleBillDateSubmit}>
              <input
                type="date"
                value={billDate}
                onChange={(e) => setBillDate(e.target.value)}
                required
              />
              <button type="submit">Submit</button>
            </form>
          </div>
        </div>
      )}

      {/* Document Preview */}
      <div className="print-area space-y-8">
        {printMode === 'invoice' && (
          <div dangerouslySetInnerHTML={{ __html: generateInvoice() }} />
        )}
        {printMode === 'certificate' && (
          <div dangerouslySetInnerHTML={{ __html: generateCertificate() }} />
        )}
        {printMode === 'performance' && (
          <div dangerouslySetInnerHTML={{ __html: generatePerformanceGuarantee() }} />
        )}
        {printMode === 'test' && (
          <div dangerouslySetInnerHTML={{ __html: generateTestResults() }} />
        )}
        {printMode === 'all' && (
          <>
            <div dangerouslySetInnerHTML={{ __html: generateInvoice() }} />
            <div className="page-break"></div>
            <div dangerouslySetInnerHTML={{ __html: generateCertificate() }} />
            <div className="page-break"></div>
            <div dangerouslySetInnerHTML={{ __html: generatePerformanceGuarantee() }} />
            <div className="page-break"></div>
            <div dangerouslySetInnerHTML={{ __html: generateTestResults() }} />
          </>
        )}
      </div>
    </div>
  );
};

export default BillPrintView;